import * as React from "react";
import { cn } from "@/lib/utils";
import { Check, Circle, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface Step {
  id: string;
  title: string;
  description?: string;
  optional?: boolean;
}

interface StepperProps {
  steps: Step[];
  currentStep: number;
  onStepClick?: (stepIndex: number) => void;
  className?: string;
  orientation?: "horizontal" | "vertical";
}

export function Stepper({
  steps,
  currentStep,
  onStepClick,
  className,
  orientation = "horizontal",
}: StepperProps) {
  const isVertical = orientation === "vertical";

  return (
    <div
      className={cn(
        "flex",
        isVertical ? "flex-col gap-0" : "items-center gap-2",
        className
      )}
    >
      {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isCurrent = index === currentStep;
        const isClickable = onStepClick && (isCompleted || index <= currentStep);

        return (
          <React.Fragment key={step.id}>
            <div
              className={cn(
                "flex items-center gap-3",
                isVertical && "relative pb-8 last:pb-0"
              )}
            >
              {/* Linea vertical */}
              {isVertical && index < steps.length - 1 && (
                <div
                  className={cn(
                    "absolute left-[15px] top-[32px] w-0.5 h-[calc(100%-24px)]",
                    isCompleted ? "bg-primary" : "bg-muted"
                  )}
                />
              )}

              {/* Circulo del paso */}
              <button
                type="button"
                onClick={() => isClickable && onStepClick?.(index)}
                disabled={!isClickable}
                className={cn(
                  "relative z-10 flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all",
                  isCompleted && "bg-primary border-primary text-primary-foreground",
                  isCurrent && "border-primary bg-primary/10 text-primary",
                  !isCompleted && !isCurrent && "border-muted bg-background text-muted-foreground",
                  isClickable && "cursor-pointer hover:scale-110",
                  !isClickable && "cursor-default"
                )}
              >
                {isCompleted ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <span className="text-sm font-medium">{index + 1}</span>
                )}
              </button>

              {/* Texto del paso */}
              <div className="flex flex-col">
                <span
                  className={cn(
                    "text-sm font-medium",
                    isCurrent && "text-primary",
                    isCompleted && "text-foreground",
                    !isCompleted && !isCurrent && "text-muted-foreground"
                  )}
                >
                  {step.title}
                  {step.optional && (
                    <span className="text-xs text-muted-foreground ml-1">(Opcional)</span>
                  )}
                </span>
                {step.description && (
                  <span className="text-xs text-muted-foreground">{step.description}</span>
                )}
              </div>
            </div>

            {/* Linea horizontal entre pasos */}
            {!isVertical && index < steps.length - 1 && (
              <div
                className={cn(
                  "flex-1 h-0.5 min-w-[40px]",
                  isCompleted ? "bg-primary" : "bg-muted"
                )}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// Componente contenedor con navegacion
interface StepperContainerProps {
  steps: Step[];
  children: React.ReactNode[];
  onComplete?: () => void;
  onCancel?: () => void;
  className?: string;
}

export function StepperContainer({
  steps,
  children,
  onComplete,
  onCancel,
  className,
}: StepperContainerProps) {
  const [currentStep, setCurrentStep] = React.useState(0);

  const canGoNext = currentStep < steps.length - 1;
  const canGoPrev = currentStep > 0;
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = () => {
    if (canGoNext) setCurrentStep(currentStep + 1);
    else onComplete?.();
  };

  const handlePrev = () => {
    if (canGoPrev) setCurrentStep(currentStep - 1);
  };

  return (
    <div className={cn("space-y-6", className)}>
      <Stepper
        steps={steps}
        currentStep={currentStep}
        onStepClick={setCurrentStep}
      />

      <div className="min-h-[200px]">
        {children[currentStep]}
      </div>

      <div className="flex justify-between pt-4 border-t">
        <div>
          {onCancel && (
            <Button variant="ghost" onClick={onCancel}>
              Cancelar
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          {canGoPrev && (
            <Button variant="outline" onClick={handlePrev}>
              Anterior
            </Button>
          )}
          <Button onClick={handleNext}>
            {isLastStep ? "Completar" : "Siguiente"}
            {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Hook para manejar el stepper
export function useStepper(totalSteps: number) {
  const [currentStep, setCurrentStep] = React.useState(0);

  const next = () => setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
  const prev = () => setCurrentStep((prev) => Math.max(prev - 1, 0));
  const goTo = (step: number) => setCurrentStep(Math.max(0, Math.min(step, totalSteps - 1)));
  const reset = () => setCurrentStep(0);

  const isFirst = currentStep === 0;
  const isLast = currentStep === totalSteps - 1;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  return {
    currentStep,
    next,
    prev,
    goTo,
    reset,
    isFirst,
    isLast,
    progress,
  };
}

// Stepper compacto para espacios reducidos
interface CompactStepperProps {
  steps: string[];
  currentStep: number;
  className?: string;
}

export function CompactStepper({ steps, currentStep, className }: CompactStepperProps) {
  return (
    <div className={cn("flex items-center gap-1", className)}>
      {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isCurrent = index === currentStep;

        return (
          <React.Fragment key={index}>
            <div className="flex items-center gap-1.5">
              <div
                className={cn(
                  "flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium transition-all",
                  isCompleted && "bg-primary text-primary-foreground",
                  isCurrent && "bg-primary/20 text-primary border-2 border-primary",
                  !isCompleted && !isCurrent && "bg-muted text-muted-foreground"
                )}
              >
                {isCompleted ? (
                  <Check className="w-3 h-3" />
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>
              <span
                className={cn(
                  "text-xs font-medium hidden sm:inline",
                  isCurrent && "text-primary",
                  !isCurrent && "text-muted-foreground"
                )}
              >
                {step}
              </span>
            </div>
            {index < steps.length - 1 && (
              <ChevronRight
                className={cn(
                  "w-4 h-4 flex-shrink-0",
                  isCompleted ? "text-primary" : "text-muted-foreground/50"
                )}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
